import { useEffect } from "react";

import { EditorContent } from "@tiptap/react";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";

import { devTools } from "@/components/debug-panel/DevToolsStore";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { pyodideManager } from "@/components/pyodide/PyodideManager";
import { ChangeRequestToolbar } from "@/components/tiptap/ChangeRequestToolbar";
import { EditorPopoverView } from "@/components/tiptap/editor-popover/EditorPopoverView";
import { editorExtensions } from "@/components/tiptap/extensions";
import { GridControlsOverlay } from "@/components/tiptap/grid-controls/GridControlsOverlay";
import { HEADINGS } from "@/components/tiptap/Heading";
import { LineOptionButtonsOverlay } from "@/components/tiptap/LineOptionButtonsLayer";
import { LockIndicatorsOverlay } from "@/components/tiptap/lock/LockIndicatorsOverlay";
import { TiptapSuggestions } from "@/components/tiptap/tiptap-suggestions/TiptapSuggestions";
import { useStore } from "@/store";
import { getIsTouchDevice } from "@/utils/getIsTouchDevice";
import { twMerge } from "@/utils/twMerge";

import { useEditorCommon } from "./utils/useEditorCommon";
import { useEditorStateCommon } from "./utils/useEditorStateCommon";
import { useKeepTouchMomentum } from "./utils/useKeepTouchMomentum";
import { useKeyboardAwareScroll } from "./utils/useKeyboardAwareScroll";
import { hasMobileBridgeMethod, useMobileBridge } from "./utils/useMobileBridge";

import type { TextNodeModel } from "@/models/nodes/TextNodeModel";
import type { Level } from "@tiptap/extension-heading";

type TiptapProps = {
  node: TextNodeModel;
  className?: string;
};

const TiptapEditor = observer(function TiptapEditor({ node: textNode, className }: TiptapProps) {
  const t = useTranslations();

  const rootStore = useStore();
  const { documentTree, auth, view, userSettings } = rootStore;
  const { isLoggedIn } = auth;
  const { isMobileWebView } = view;
  const isTouchDevice = getIsTouchDevice();

  const { doc } = textNode;
  const { editable } = doc;
  const isStudent = rootStore.classroom.isStudentIn(doc.team);
  // Read in render so the observer re-runs the effect when the doc fetch
  // completes; inside the effect the read would go untracked.
  const classroomPending = rootStore.classroom.enabled && !doc.api.isFetched;

  const tiptapTranslations = useTranslations("tiptap");

  const editorClassName = twMerge(
    "h-full py-14 px-12 pb-[50vh] focus:outline-none",
    "sm:max-w-[100vw] sm:px-6 sm:pt-7",
    isLoggedIn ? "sm:mt-10" : "sm:mt-20",
    // Native chrome lives outside the WebView, so the top spacing reserved
    // for the floating MobileHeader / login banner is dead space here — drop
    // it. Keep a tall bottom buffer: native auto-scroll is disabled, so
    // useKeyboardAwareScroll scrolls the page itself to lift the caret above
    // the keyboard, and the last lines need ~a keyboard height of scroll room.
    isMobileWebView && "py-2 px-4 pb-[50vh] sm:pt-2 sm:mt-0",
  );

  const editor = useEditorCommon({
    doc,
    editable,
    extensions: editorExtensions({
      doc,
      translator: tiptapTranslations,
      defaultPageTitle: t("meta.title"),
      onTitleChange: (newTitle: string) => documentTree.setCurrentDocTitle(newTitle),
      placeholder: (node) => {
        if (node.type.name === "paragraph") {
          const { alignment, indent, firstLineIndent } = node.attrs as {
            alignment?: string;
            indent?: number;
            firstLineIndent?: boolean;
          };
          if (alignment === "center" || alignment === "right" || indent || firstLineIndent) {
            return undefined;
          }
          return t("doc.text.placeholder");
        }

        if (node.type.name === "heading") {
          const { level } = node.attrs as { level: Level };
          const { placeholder } =
            level in HEADINGS ? HEADINGS[level as keyof typeof HEADINGS] : HEADINGS[3];
          return t(`doc.${placeholder}`);
        }

        return undefined;
      },
      auth,
      rootStore,
    }),
    editorProps: {
      attributes: {
        class: editorClassName,
      },
    },
  });

  const editorState = useEditorStateCommon({ editor });

  const pathname = usePathname();

  useEffect(() => {
    const isDailyChallenge = pathname?.includes("/dailychallenge");

    if (doc.editable && !isDailyChallenge && !classroomPending && !isStudent) {
      pyodideManager.init();
    }

    if (editor && editorState) {
      runInAction(() => {
        // `doc.editor.tiptap` is the shared handle that the rest of the app
        // reads to operate on this editor (suggestion popups, chain commands,
        // selection queries, the mobile bridge, etc.). It must always point
        // to the live editor instance owned by the currently mounted Tiptap.
        //
        // The instance can change under us — StrictMode, HMR, and route
        // remounts all unmount this component and create a new editor on
        // remount. We assign here only when the reference is actually stale,
        // because this effect also re-runs on every keystroke (editorState
        // is in the deps), and an unconditional write would wake every MobX
        // observer of doc.editor.tiptap on each character.
        if (doc.editor.tiptap !== editor) {
          doc.editor.tiptap = editor;
        }

        doc.editor.updateEditorState(editorState);

        doc.connectionStatus.setupEditorChangeTracking(editor);
      });
    }
  }, [doc, editor, editorState, pathname, isStudent, classroomPending]);

  // A duplicated doc opens with an attributed title ("Title [User Name copy]")
  // stashed by the duplicate action; the doc is synced before the editor
  // mounts, so the rename replaces the copied title instead of racing it.
  useEffect(() => {
    if (!editor || !doc.id) {
      return;
    }
    const pendingTitle = documentTree.consumePendingCopyTitle(doc.id);
    if (pendingTitle !== undefined) {
      editor.commands.setDocumentTitle(pendingTitle);
    }
  }, [doc, documentTree, editor]);

  useMobileBridge({ editor, doc });
  useKeyboardAwareScroll(doc);
  useKeepTouchMomentum(editor);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={twMerge("flex flex-col h-full font-sans text-size-16", className)}
      onClick={(e) => {
        // A click in math places the math cursor on mousedown; resetting would undo it.
        if (e.target instanceof Element && e.target.closest("[data-math]")) {
          return;
        }

        if (!(e.target instanceof HTMLInputElement)) {
          doc.editor.resetInput(true);
        }
        doc.editor.suggestions.reset();
      }}
    >
      <div className="line-number-reset absolute w-0 h-0" key={editorState?.lineCount} />
      <NodeStyleProvider debugOutlineNodes={devTools.outlineNodes}>
        <EditorContent
          editor={editor}
          className={twMerge(
            "h-full",
            userSettings.codeTheme === "light" &&
              "[&_*_code]:dark:text-black [&_*_code]:dark:bg-gray-100",
            userSettings.codeTheme === "dark" && "[&_*_code]:text-white [&_*_code]:bg-gray-900",
          )}
        />
        {doc && <LineOptionButtonsOverlay doc={doc} />}
        {doc && <LockIndicatorsOverlay doc={doc} />}
        {(isMobileWebView ? hasMobileBridgeMethod("openGridMenu") : isTouchDevice) && (
          <GridControlsOverlay doc={doc} />
        )}
      </NodeStyleProvider>
      <ChangeRequestToolbar editor={editor} />
      {doc.editor.tiptap && <TiptapSuggestions doc={doc} />}
      {doc.editor.tiptap && <EditorPopoverView doc={doc} />}
    </div>
  );
});

export function Tiptap(props: TiptapProps) {
  const locale = useLocale();
  return <TiptapEditor key={locale} {...props} />;
}
